// hooks/useMessageInteractions.js
import { useState, useCallback, useEffect } from 'react';

export const useMessageInteractions = (activeConversationId, activeMessages) => {
    const [replyingToMessage, setReplyingToMessage] = useState(null);
    const [selectModeActive, setSelectModeActive] = useState(false);
    const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());

    // Reset state when conversation changes
    useEffect(() => {
        setReplyingToMessage(null);
        setSelectModeActive(false);
        setSelectedMessageIds(new Set());
    }, [activeConversationId]);

    const handleReplyToMessage = useCallback((message) => {
        if (message) { setReplyingToMessage(message); }
        else { setReplyingToMessage(null); }
    }, []);

    const handleCancelReply = useCallback(() => { setReplyingToMessage(null); }, []);

    const handleStartSelectFromMenu = useCallback((messageId) => {
        if (!messageId) return;
        setSelectModeActive(true);
        setSelectedMessageIds(new Set([messageId]));
    }, []);

    const handleToggleMessageSelection = useCallback((messageId) => {
        if (!selectModeActive) return;
        setSelectedMessageIds(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(messageId)) {
                newSelected.delete(messageId);
                if (newSelected.size === 0) { setSelectModeActive(false); } // Auto-exit if empty
            } else { newSelected.add(messageId); }
            return newSelected;
        });
    }, [selectModeActive]);

    const handleCancelSelectMode = useCallback(() => {
        setSelectModeActive(false);
        setSelectedMessageIds(new Set());
    }, []);

    const handleSelectAllMessages = useCallback((isChecked) => {
        if (!selectModeActive) return;
        if (isChecked) {
            const allMessageIds = activeMessages.filter(msg => msg.type !== 'date').map(msg => msg.id);
            setSelectedMessageIds(new Set(allMessageIds));
        } else { setSelectedMessageIds(new Set()); }
    }, [selectModeActive, activeMessages]);

    const isMessageSelected = useCallback((messageId) => selectedMessageIds.has(messageId), [selectedMessageIds]);

    return {
        replyingToMessage, setReplyingToMessage: handleReplyToMessage, handleCancelReply,
        selectModeActive, setSelectModeActive, selectedMessageIds, setSelectedMessageIds,
        handleStartSelectFromMenu, handleToggleMessageSelection, handleCancelSelectMode,
        handleSelectAllMessages, isMessageSelected,
    };
};